import React, { SyntheticEvent, useEffect, useState, ChangeEvent } from 'react';

import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import Title from '../../../components/Title';
import axios from 'axios';
import CancelIcon from '@mui/icons-material/Cancel';
import { Type_Country } from '../../../models/type_country';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const OficinasEdit: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [type_country_id, setCountry_id] = useState('');
  const [city, setCity] = useState('');
  const [manager, setManager] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [country, setCountry] = useState([]);
  const [isModified, setIsModified] = useState(false);
  const [initialState, setInitialState] = useState({
    name: '',
    phone: '',
    address: '',
    type_country_id: '',
    city: '',
    manager: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      showLoading();

      try {
        const { data: countryData } = await axios.get('typecountry');
        setCountry(countryData.data);

        const { data: officeData } = await axios.get(`offices/${id}`);
        const initialData = {
          name: officeData.name,
          phone: officeData.phone,
          address: officeData.address,
          type_country_id: officeData.type_country.id,
          city: officeData.city,
          manager: officeData.manager,
        };
        setInitialState(initialData);
        setName(officeData.name);
        setPhone(officeData.phone);
        setAddress(officeData.address);
        setCity(officeData.city);
        setManager(officeData.manager);
        setCountry_id(officeData.type_country.id);

        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
      } catch (error) {
        showAlert('Error fetching data!', 'error');
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<any>>, fieldName: keyof typeof initialState) => (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => {
    setter(event.target.value as string);
    if (initialState[fieldName] !== event.target.value) {
      setIsModified(true);
    } else {
      setIsModified(false);
    }
  };

  const handleSelectChange = (setter: React.Dispatch<React.SetStateAction<any>>, fieldName: keyof typeof initialState) => (event: SelectChangeEvent) => {
    setter(event.target.value as string);
    if (initialState[fieldName] !== event.target.value) {
      setIsModified(true);
    } else {
      setIsModified(false);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!name || !phone || !address || !city) {
      showAlert('Tiene campos pendientes por diligenciar!', 'warning');
      return;
    }

    const formData = {
      name,
      phone,
      address,
      city,
      manager,
      type_country_id
    };

    const url = `offices/${id}`;
    try {
      showLoading();
      const response = await axios.put(url, formData);
      showAlert('Se ha realizado la actualización exitosamente!', 'success');
      await new Promise(response => setTimeout(response, 1000));
      navigate('/administrar/oficinas');
    } catch (error) {
      showAlert('Error fetching data!', 'error');
    } finally {
      hideLoading();
    }
  };

  document.title = "Editar Oficinas - Route App";

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Información Oficinas</Title>
      </Box>
      <Card>
        <Divider sx={{ margin: 0 }} />
        <Box component="form" noValidate onSubmit={submit} sx={{ mt: 1 }}>
          <CardContent>
            <Grid container spacing={2}>
              {showError && (
                <Grid container alignItems="center" justifyContent="center" height="100%">
                  <Grid item xs={12} sm={8} md={5}>
                    <Alert severity="error" onClick={() => setShowError(false)}>
                      {error}
                      <CancelIcon fontSize="inherit" />
                    </Alert>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  size='small'
                  fullWidth
                  label='Nombre de Oficina'
                  placeholder='Leonard'
                  id="name"
                  value={name}
                  onChange={handleInputChange(setName, 'name')}
                  inputProps={{ maxLength: 30 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size='small'
                  fullWidth
                  type='tel'
                  label='Teléfono'
                  placeholder='6021234567'
                  id="phone"
                  value={phone}
                  onChange={handleInputChange(setPhone, 'phone')}
                  inputProps={{ maxLength: 10 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size='small'
                  fullWidth
                  label='Dirección'
                  placeholder='Cl 10 45-10'
                  id="address"
                  value={address}
                  onChange={handleInputChange(setAddress, 'address')}
                  inputProps={{ maxLength: 30 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size='small'
                  fullWidth
                  label='Ciudad'
                  placeholder='Medellín'
                  id="city"
                  value={city}
                  onChange={handleInputChange(setCity, 'city')}
                  inputProps={{ maxLength: 30 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id='form-layouts-separator-select-label'>Seleccionar País</InputLabel>
                  {country && country.length > 0 ? (
                    <Select
                      label='País'
                      size='small'
                      value={type_country_id}
                      id='form-layouts-separator-select'
                      labelId='form-layouts-separator-select-label'
                      onChange={handleSelectChange(setCountry_id, 'type_country_id')}
                      required
                    >
                      {country.map((country: Type_Country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          <Divider sx={{ margin: 0 }} />
          <CardActions>
            <Button size='small' type='submit' sx={{ mr: 2 }} variant='contained' onClick={submit} disabled={!isModified}>
              Guardar
            </Button>
            <Link to='/administrar/oficinas'>
              <Button size='small' color='secondary' variant='outlined'>
                Cancelar
              </Button>
            </Link>
          </CardActions>
        </Box>
      </Card>
    </HeaderResponsive>
  );
}

export default OficinasEdit;
